import type { Reducer } from 'redux';

import type { MyGroupsLoadAction, MyGroupsLoadMoreAction } from '../../domains/myGroups/actions.types';
import type {
  AddFavoriteCommunityFulfilledAction,
  RemoveFavoriteCommunityFulfilledAction,
} from '../../domains/viewerCommunityRelationships/actions.types';

import type { FilteredMyGroups, MyGroups, MyGroupsStateKey } from './types';
import { getMyGroupsStateKey } from './types';

export type MyGroupsAction =
  | MyGroupsLoadAction
  | MyGroupsLoadMoreAction
  | AddFavoriteCommunityFulfilledAction
  | RemoveFavoriteCommunityFulfilledAction;

const initialFilteredGroups: FilteredMyGroups = {
  groupIds: [],
  favoriteGroupIds: [],
  cursor: null,
  hasNextPage: false,
  status: 'pending',
};

const loadPendingMyGroups = (
  state: MyGroups,
  stateKey: MyGroupsStateKey,
  requestGeneration: number | undefined
): MyGroups => ({
  ...state,
  [stateKey]: {
    ...(state[stateKey] ?? initialFilteredGroups),
    status: 'pending',
    ...(requestGeneration === undefined ? {} : { requestGeneration }),
  },
});

const loadRejectedMyGroups = (state: MyGroups, stateKey: MyGroupsStateKey, requestGeneration: number | undefined) => {
  if (state[stateKey]?.requestGeneration !== requestGeneration) {
    return state;
  }

  const rejected: FilteredMyGroups = {
    ...(state[stateKey] ?? initialFilteredGroups),
    status: 'rejected',
    ...(requestGeneration === undefined ? {} : { requestGeneration }),
  };

  return {
    ...state,
    [stateKey]: rejected,
  };
};

const loadMoreMyGroupsFulfilled = (state: MyGroups, stateKey: MyGroupsStateKey, data: FilteredMyGroups) => {
  const currentGroupIds = state[stateKey]?.groupIds ?? [];
  const currentFavoriteGroupIds = state[stateKey]?.favoriteGroupIds ?? [];
  const groupIds = [...currentGroupIds, ...data.groupIds];
  const favoriteGroupIds = currentFavoriteGroupIds.length > 0 ? currentFavoriteGroupIds : data.favoriteGroupIds;

  return {
    ...state,
    [stateKey]: {
      ...data,
      groupIds,
      favoriteGroupIds,
      ...(state[stateKey]?.requestGeneration === undefined
        ? {}
        : { requestGeneration: state[stateKey]?.requestGeneration }),
    },
  };
};

const applyToAllBuckets =
  (reducerWithKey: (state: MyGroups, action: MyGroupsAction, stateKey: MyGroupsStateKey) => MyGroups) =>
  (state: MyGroups, action: MyGroupsAction) =>
    Object.keys(state).reduce((state, stateKey: MyGroupsStateKey) => reducerWithKey(state, action, stateKey), state);

const addFavoriteFulfilled = applyToAllBuckets(
  (state: MyGroups, action: AddFavoriteCommunityFulfilledAction, stateKey: MyGroupsStateKey) => {
    const bucket = state[stateKey];
    if (
      !bucket ||
      !bucket.groupIds.includes(action.payload.id) ||
      bucket.favoriteGroupIds.includes(action.payload.id)
    ) {
      return state;
    }

    return {
      ...state,
      [stateKey]: {
        ...bucket,
        favoriteGroupIds: [...bucket.favoriteGroupIds, action.payload.id],
      },
    };
  }
);

const removeFavoriteFulfilled = applyToAllBuckets(
  (state: MyGroups, action: RemoveFavoriteCommunityFulfilledAction, stateKey: MyGroupsStateKey) => {
    const bucket = state[stateKey];
    if (!bucket?.favoriteGroupIds.includes(action.payload.id)) {
      return state;
    }

    return {
      ...state,
      [stateKey]: {
        ...bucket,
        favoriteGroupIds: bucket.favoriteGroupIds.filter((id) => id !== action.payload.id),
      },
    };
  }
);

const initialState: MyGroups = {};

const reducer: Reducer<MyGroups, MyGroupsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'MY_GROUPS.LOAD_PENDING':
      return loadPendingMyGroups(
        state,
        getMyGroupsStateKey(action.payload.filteredBy, action.payload.bridgeToViewerCommunityIdSets),
        action.payload.requestGeneration
      );
    case 'MY_GROUPS.LOAD_FULFILLED': {
      const stateKey = getMyGroupsStateKey(action.payload.filteredBy, action.payload.bridgeToViewerCommunityIdSets);
      if (state[stateKey]?.requestGeneration !== action.payload.requestGeneration) {
        return state;
      }

      return {
        ...state,
        [stateKey]: {
          ...action.payload.entities.myGroups,
          ...(action.payload.requestGeneration === undefined
            ? {}
            : { requestGeneration: action.payload.requestGeneration }),
        },
      };
    }
    case 'MY_GROUPS.LOAD_REJECTED':
      return loadRejectedMyGroups(
        state,
        getMyGroupsStateKey(action.payload.filteredBy, action.payload.bridgeToViewerCommunityIdSets),
        action.payload.requestGeneration
      );
    case 'MY_GROUPS.LOAD_MORE_FULFILLED': {
      const stateKey = getMyGroupsStateKey(action.payload.filteredBy, action.payload.bridgeToViewerCommunityIdSets);
      if (state[stateKey]?.requestGeneration !== action.payload.requestGeneration) {
        return state;
      }

      return loadMoreMyGroupsFulfilled(state, stateKey, action.payload.entities.myGroups);
    }
    case 'COMMUNITY.ADD_FAVORITE_FULFILLED':
      return addFavoriteFulfilled(state, action);
    case 'COMMUNITY.REMOVE_FAVORITE_FULFILLED':
      return removeFavoriteFulfilled(state, action);
    default:
      return state;
  }
};

export default reducer;
